import React from 'react'
import { Button } from '@/components/ui/button'
import { AlertDialog, AlertDialogContent, AlertDialogTrigger, AlertDialogCancel, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog'
import { z } from 'zod'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { Slider } from '@/components/ui/slider'
import { toast } from 'sonner'
import { useMutation } from '@tanstack/react-query'
import { useTRPC } from '@/trpc'
import { TRPCClientError } from '@trpc/client'
import { TContactCreate } from '@/types/trpc.types'
import * as Icons from 'lucide-react'
import MultiSelect from '@/components/ui/multi-select'

interface Props {
    trigger?: React.ReactNode
    onCreated?: (newContact:TContactCreate) => void
}

const createContactSchema = z.object({
    contact_name: z.string().min(1, "Naam is verplicht"),
    email: z.string().optional(),
    telephone: z.string().optional(),
    contact_via: z.string().array().optional(), // email, telephone, whatsapp, other
    note: z.string().optional(),
    reliability: z.number().min(0).max(5).optional(),
    quality_players: z.number().min(0).max(5).optional(),
    company_name: z.string().optional(),
})
export type CreateContactSchema = z.infer<typeof createContactSchema>;

const CreateContact = ({ trigger, onCreated }: Props) => {
    const { trpc } = useTRPC();
    const [open, setOpen] = React.useState(false);

    const form = useForm<CreateContactSchema>({
        resolver: zodResolver(createContactSchema),
    })
    const formValues = form.watch()
    const isFormValid = createContactSchema.safeParse(formValues).success;

    const createMutation = useMutation({
        mutationFn: async (data:CreateContactSchema) => {
            const newContact = await trpc.contacts.create.mutate(data)
            return newContact as TContactCreate
        },
        onSuccess: (data) => {
            onCreated?.(data);
            toast.success("Contact succesvol aangemaakt");
            setOpen(false);
            form.reset();
        },
        onError: (err) => {
            if(err instanceof Error) {
                toast.error(err.message);
            } else if(err instanceof TRPCClientError) {
                toast.error(err.message);
            } else {
                toast.error("Er is een onbekende fout opgetreden.");
            }
        }
    })

    const handleSubmit = () => {
        const values = form.getValues();
        const isValid = createContactSchema.safeParse(values).success;
        if(!isValid) {
            toast.error("Formulier is niet correct");
            return;
        }
        createMutation.mutate(values);
    }

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
        <AlertDialogContent className='max-h-[90dvh] overflow-auto'>
            <AlertDialogHeader>
                <AlertDialogTitle>Nieuw contact</AlertDialogTitle>
                <AlertDialogDescription>Vul de velden in om een nieuw contact aan te maken</AlertDialogDescription>
            </AlertDialogHeader>
            <div className='flex flex-col gap-4'>
                <div>
                    <Label required>Naam</Label>
                    <Input {...form.register("contact_name")} />
                </div>
                <div>
                    <Label>Bedrijfsnaam</Label>
                    <Input {...form.register("company_name")} />
                </div>
                <div>
                    <Label>Email</Label>
                    <Input {...form.register("email")} />
                </div>
                <div>
                    <Label>Telefoon</Label>
                    <Input {...form.register("telephone")} />
                </div>
                <div>
                    <Label>Contact via</Label>
                    <Controller 
                        control={form.control}
                        name="contact_via"
                        render={({ field }) => (
                            <MultiSelect 
                                selectedOptions={field.value || []}
                                onValueChange={(v)=>{field.onChange(v)}}
                                options={[
                                    { value: "email", label: "Email" },
                                    { value: "telephone", label: "Telefoon" },
                                    { value: "whatsapp", label: "WhatsApp" },
                                    { value: "other", label: "Andere" },
                                ]}
                                placeholder='Contact via...'
                            />
                        )}
                    />
                </div>
                <div>
                    <Label>Notitie</Label>
                    <Textarea {...form.register("note")} />
                </div>
                <div>
                    <Label>Betrouwbaarheid</Label>
                    <Controller
                        control={form.control}
                        name="reliability"
                        render={({ field }) => (
                            <div className='flex items-center gap-2'>
                                <p className='text-xs font-medium bg-muted px-2 py-1 rounded-sm'>{field.value || 0}/5</p>
                                <Slider 
                                    value={field.value ? [field.value] : undefined}
                                    onValueChange={(v)=>{ field.onChange(v[0]) }}
                                    min={0}
                                    max={5}
                                    step={1}
                                />
                            </div>
                        )}
                    />
                </div>
                <div>
                    <Label>Kwaliteit spelers</Label>
                    <Controller
                        control={form.control}
                        name="quality_players"
                        render={({ field }) => (
                            <div className='flex items-center gap-2'>
                                <p className='text-xs font-medium bg-muted px-2 py-1 rounded-sm'>{field.value || 0}/5</p>
                                <Slider 
                                    value={field.value ? [field.value] : undefined}
                                    onValueChange={(v)=>{ field.onChange(v[0]) }}
                                    min={0}
                                    max={5}
                                    step={1}
                                />
                            </div>
                        )}
                    />
                </div>
            </div>
            <AlertDialogFooter>
                <AlertDialogCancel>Annuleren</AlertDialogCancel>
                <Button
                    disabled={!isFormValid || createMutation.isLoading}
                    onClick={()=>{handleSubmit()}}
                >
                    {createMutation.isLoading && <Icons.Loader2 className="w-4 h-4 animate-spin" />}
                    Opslaan
                </Button>
            </AlertDialogFooter>
        </AlertDialogContent>
    </AlertDialog>
  )
}

export default CreateContact